/**
 * Form Renderer Component
 *
 * Renders form fields from JSON schema for public submission.
 * Supports all 15 field types:
 * - Text, Email, Phone, Number, Date, Time
 * - Dropdown, Radio, Checkbox, File Upload
 * - Signature field, Section header, Divider, Spacer, Textarea
 *
 * Features:
 * - Validation (required, patterns, min/max)
 * - Mobile-optimized touch targets (44px min)
 * - Accessibility (ARIA labels, keyboard navigation)
 */
import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  ChevronDown,
  Upload,
  X,
  Check,
  AlertCircle,
} from 'lucide-react';
import { Input, Button, Spinner } from '@respectly/ui';
import type { FormField } from '@respectly/shared-types';
import { normalizeToE164 } from '@respectly/utils';
import { SignatureCapture } from './SignatureCapture';

interface InputBrandingStyle {
  borderRadius?: number;
  borderColor?: string;
  backgroundColor?: string;
  textColor?: string;
}

interface FormRendererProps {
  fields: FormField[];
  layout?: 'single' | 'two-column';
  onSubmit: (data: Record<string, unknown>) => Promise<void>;
  submitButtonText?: string;
  isSubmitting?: boolean;
  onFileUpload?: (file: File) => Promise<string>; // Returns file URL
  inputStyle?: InputBrandingStyle;
}

interface FormErrors {
  [fieldId: string]: string;
}

function getFieldColSpan(field: FormField, layout?: 'single' | 'two-column'): string {
  if (layout !== 'two-column') return '';
  const fullWidth: FormField['type'][] = [
    'section_header', 'divider', 'spacer', 'textarea', 'file', 'signature', 'radio', 'checkbox',
  ];
  return fullWidth.includes(field.type) ? 'col-span-2' : '';
}

export function FormRenderer({
  fields,
  layout = 'single',
  onSubmit,
  submitButtonText = 'Submit',
  isSubmitting = false,
  onFileUpload,
  inputStyle,
}: FormRendererProps) {
  // Build input styling from branding
  const inputBrandingStyle: React.CSSProperties = {
    ...(inputStyle?.borderRadius !== undefined && {
      borderRadius: `${inputStyle.borderRadius}px`,
    }),
    ...(inputStyle?.borderColor && {
      borderColor: inputStyle.borderColor,
    }),
    ...(inputStyle?.backgroundColor && {
      backgroundColor: inputStyle.backgroundColor,
    }),
    ...(inputStyle?.textColor && {
      color: inputStyle.textColor,
    }),
  };
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [activeSignatureField, setActiveSignatureField] = useState<string | null>(null);
  const [uploadingFields, setUploadingFields] = useState<Set<string>>(new Set());
  const fileInputRefs = useRef<Map<string, HTMLInputElement>>(new Map());

  const handleFieldChange = (fieldId: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));

    // Clear error when user types
    if (errors[fieldId]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  const handleBlur = (fieldId: string) => {
    setTouched((prev) => new Set(prev).add(fieldId));

    // Validate on blur
    const field = fields.find((f) => f.id === fieldId);
    if (field) {
      const error = validateField(field, formData[fieldId]);
      if (error) {
        setErrors((prev) => ({ ...prev, [fieldId]: error }));
      }
    }
  };

  const validateField = (field: FormField, value: unknown): string | null => {
    const validation = field.validation;
    const strValue = String(value ?? '').trim();

    // Required check
    if (validation?.required && !strValue) {
      return validation.customMessage || `${field.label} is required`;
    }

    if (!strValue) return null;

    // Min/max length for strings
    if (validation?.minLength && strValue.length < validation.minLength) {
      return `Minimum ${validation.minLength} characters required`;
    }
    if (validation?.maxLength && strValue.length > validation.maxLength) {
      return `Maximum ${validation.maxLength} characters allowed`;
    }

    // Min/max for numbers
    if (field.type === 'number' && strValue) {
      const numValue = Number(strValue);
      if (validation?.min !== undefined && numValue < validation.min) {
        return `Minimum value is ${validation.min}`;
      }
      if (validation?.max !== undefined && numValue > validation.max) {
        return `Maximum value is ${validation.max}`;
      }
    }

    // Pattern validation
    if (validation?.pattern) {
      try {
        const regex = new RegExp(validation.pattern);
        if (!regex.test(strValue)) {
          return validation.customMessage || 'Invalid format';
        }
      } catch {
        // Invalid regex, skip
      }
    }

    // Email validation
    if (field.type === 'email' && strValue) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(strValue)) {
        return 'Please enter a valid email address';
      }
    }

    // Phone validation (libphonenumber-js — must parse as E.164 or a valid US national number)
    if (field.type === 'phone' && strValue) {
      if (!normalizeToE164(strValue).ok) {
        return 'Please enter a valid phone number with country code (e.g. +1 555 123 4567)';
      }
    }

    return null;
  };

  const validateAllFields = (): boolean => {
    const newErrors: FormErrors = {};

    for (const field of fields) {
      // Skip non-input fields
      if (['section_header', 'divider', 'spacer'].includes(field.type)) {
        continue;
      }

      const error = validateField(field, formData[field.id]);
      if (error) {
        newErrors[field.id] = error;
      }
    }

    setErrors(newErrors);
    setTouched(new Set(fields.map((f) => f.id)));
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateAllFields()) {
      // Scroll to first error
      const firstErrorField = document.querySelector('[data-error="true"]');
      firstErrorField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    await onSubmit(formData);
  };

  const handleFileChange = async (fieldId: string, file: File | null) => {
    if (!file || !onFileUpload) return;

    setUploadingFields((prev) => new Set(prev).add(fieldId));

    try {
      const url = await onFileUpload(file);
      handleFieldChange(fieldId, { url, name: file.name, size: file.size });
    } catch (err) {
      console.error('File upload error:', err);
      setErrors((prev) => ({ ...prev, [fieldId]: 'Failed to upload file' }));
    } finally {
      setUploadingFields((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fieldId);
        return newSet;
      });
    }
  };

  const handleSignatureSave = (fieldId: string, signatureData: { type: string; data: string }) => {
    handleFieldChange(fieldId, signatureData);
    setActiveSignatureField(null);
  };

  // Common field wrapper
  const FieldWrapper = ({
    field,
    children,
    inGrid = false,
  }: {
    field: FormField;
    children: React.ReactNode;
    inGrid?: boolean;
  }) => {
    const hasError = touched.has(field.id) && errors[field.id];

    return (
      <div
        className={inGrid ? '' : 'mb-4'}
        data-error={hasError ? 'true' : 'false'}
      >
        {/* Label */}
        {field.label && !['section_header', 'divider', 'spacer'].includes(field.type) && (
          <label
            htmlFor={field.id}
            className="mb-2 block text-sm font-medium text-gray-700"
          >
            {field.label}
            {field.validation?.required && (
              <span className="ml-1 text-red-500">*</span>
            )}
          </label>
        )}

        {children}

        {/* Helper text */}
        {field.helpText && !hasError && (
          <p className="mt-1 text-xs text-gray-500">{field.helpText}</p>
        )}

        {/* Error message */}
        {hasError && (
          <motion.p
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-1 flex items-center gap-1 text-sm text-red-600"
          >
            <AlertCircle className="h-4 w-4" />
            {errors[field.id]}
          </motion.p>
        )}
      </div>
    );
  };

  // Render field by type
  const renderField = (field: FormField) => {
    const value = formData[field.id];
    const hasError = touched.has(field.id) && !!errors[field.id];
    const inputClass = hasError
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
      : '';

    switch (field.type) {
      case 'text':
        return (
          <FieldWrapper field={field} inGrid={layout === 'two-column'}>
            <Input
              id={field.id}
              type="text"
              value={String(value ?? '')}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              onBlur={() => handleBlur(field.id)}
              placeholder={field.placeholder}
              className={inputClass}
              style={inputBrandingStyle}
              aria-invalid={hasError}
              aria-describedby={hasError ? `${field.id}-error` : undefined}
            />
          </FieldWrapper>
        );

      case 'textarea':
        return (
          <FieldWrapper field={field} inGrid={layout === 'two-column'}>
            <textarea
              id={field.id}
              value={String(value ?? '')}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              onBlur={() => handleBlur(field.id)}
              placeholder={field.placeholder}
              rows={4}
              className={`w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 ${inputClass}`}
              style={inputBrandingStyle}
              aria-invalid={hasError}
            />
          </FieldWrapper>
        );

      case 'email':
        return (
          <FieldWrapper field={field} inGrid={layout === 'two-column'}>
            <Input
              id={field.id}
              type="email"
              value={String(value ?? '')}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              onBlur={() => handleBlur(field.id)}
              placeholder={field.placeholder || 'email@example.com'}
              className={inputClass}
              style={inputBrandingStyle}
              aria-invalid={hasError}
            />
          </FieldWrapper>
        );

      case 'phone':
        return (
          <FieldWrapper field={field} inGrid={layout === 'two-column'}>
            <Input
              id={field.id}
              type="tel"
              value={String(value ?? '')}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              onBlur={() => handleBlur(field.id)}
              placeholder={field.placeholder || '+1 (555) 123-4567'}
              className={inputClass}
              style={inputBrandingStyle}
              aria-invalid={hasError}
              aria-describedby={!field.helpText && !hasError ? `${field.id}-phone-helper` : undefined}
            />
            {!field.helpText && !hasError && (
              <p id={`${field.id}-phone-helper`} className="mt-1 text-xs text-gray-500">
                Include your country code (e.g. +1 for US, +44 for UK)
              </p>
            )}
          </FieldWrapper>
        );

      case 'number':
        return (
          <FieldWrapper field={field} inGrid={layout === 'two-column'}>
            <Input
              id={field.id}
              type="number"
              value={String(value ?? '')}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              onBlur={() => handleBlur(field.id)}
              placeholder={field.placeholder || '0'}
              min={field.validation?.min}
              max={field.validation?.max}
              className={inputClass}
              style={inputBrandingStyle}
              aria-invalid={hasError}
            />
          </FieldWrapper>
        );

      case 'date':
        return (
          <FieldWrapper field={field} inGrid={layout === 'two-column'}>
            <Input
              id={field.id}
              type="date"
              value={String(value ?? '')}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              onBlur={() => handleBlur(field.id)}
              className={inputClass}
              style={inputBrandingStyle}
              aria-invalid={hasError}
            />
          </FieldWrapper>
        );

      case 'time':
        return (
          <FieldWrapper field={field} inGrid={layout === 'two-column'}>
            <Input
              id={field.id}
              type="time"
              value={String(value ?? '')}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              onBlur={() => handleBlur(field.id)}
              className={inputClass}
              style={inputBrandingStyle}
              aria-invalid={hasError}
            />
          </FieldWrapper>
        );

      case 'dropdown':
        return (
          <FieldWrapper field={field} inGrid={layout === 'two-column'}>
            <div className="relative">
              <select
                id={field.id}
                value={String(value ?? '')}
                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                onBlur={() => handleBlur(field.id)}
                className={`w-full appearance-none rounded-md border border-gray-300 bg-white px-3 py-2 pr-10 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 ${inputClass}`}
                style={inputBrandingStyle}
                aria-invalid={hasError}
              >
                <option value="">
                  {field.placeholder || 'Select an option...'}
                </option>
                {field.options?.map((option, index) => (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            </div>
          </FieldWrapper>
        );

      case 'radio':
        return (
          <FieldWrapper field={field} inGrid={layout === 'two-column'}>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2" role="radiogroup" aria-labelledby={field.id}>
              {field.options?.map((option, index) => (
                <label
                  key={index}
                  className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border border-gray-200 p-3 transition-colors hover:bg-gray-50"
                >
                  <input
                    type="radio"
                    name={field.id}
                    value={option.value}
                    checked={value === option.value}
                    onChange={(e) => handleFieldChange(field.id, e.target.value)}
                    className="h-5 w-5 border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
          </FieldWrapper>
        );

      case 'checkbox':
        return (
          <FieldWrapper field={field} inGrid={layout === 'two-column'}>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2" role="group" aria-labelledby={field.id}>
              {field.options?.map((option, index) => {
                const checked = Array.isArray(value) && value.includes(option.value);
                return (
                  <label
                    key={index}
                    className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border border-gray-200 p-3 transition-colors hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      value={option.value}
                      checked={checked}
                      onChange={(e) => {
                        const newValue = Array.isArray(value) ? [...value] : [];
                        if (e.target.checked) {
                          newValue.push(option.value);
                        } else {
                          const idx = newValue.indexOf(option.value);
                          if (idx > -1) newValue.splice(idx, 1);
                        }
                        handleFieldChange(field.id, newValue);
                      }}
                      className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">{option.label}</span>
                  </label>
                );
              })}
            </div>
          </FieldWrapper>
        );

      case 'file': {
        const fileData = value as { url?: string; name?: string; size?: number } | null;
        const isUploading = uploadingFields.has(field.id);

        return (
          <FieldWrapper field={field} inGrid={layout === 'two-column'}>
            <input
              ref={(el) => {
                if (el) fileInputRefs.current.set(field.id, el);
              }}
              type="file"
              id={field.id}
              onChange={(e) => handleFileChange(field.id, e.target.files?.[0] || null)}
              className="hidden"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
            />

            {fileData?.url ? (
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-green-600" />
                  <span className="text-sm text-gray-700">{fileData.name}</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleFieldChange(field.id, null)}
                  className="rounded p-1 text-gray-400 hover:text-red-500"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRefs.current.get(field.id)?.click()}
                disabled={isUploading}
                className="flex min-h-[100px] w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 transition-colors hover:border-gray-400 hover:bg-gray-100"
              >
                {isUploading ? (
                  <>
                    <Spinner size="sm" />
                    <span className="mt-2 text-sm text-gray-500">Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className="h-6 w-6 text-gray-400" />
                    <span className="mt-2 text-sm font-medium text-gray-600">
                      {field.placeholder || 'Click to upload'}
                    </span>
                    <span className="mt-1 text-xs text-gray-500">
                      PDF, DOC, DOCX, JPG, PNG up to 10MB
                    </span>
                  </>
                )}
              </button>
            )}
          </FieldWrapper>
        );
      }

      case 'signature': {
        const signatureData = value as { data?: string } | null;

        return (
          <FieldWrapper field={field} inGrid={layout === 'two-column'}>
            {signatureData?.data ? (
              <div className="relative rounded-lg border bg-white p-4">
                <img
                  src={signatureData.data}
                  alt="Signature"
                  className="mx-auto max-h-24 object-contain"
                />
                <button
                  type="button"
                  onClick={() => handleFieldChange(field.id, null)}
                  className="absolute right-2 top-2 rounded p-1 text-gray-400 hover:text-red-500"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setActiveSignatureField(field.id)}
                className="flex min-h-[100px] w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-white transition-colors hover:border-primary-400 hover:bg-primary-50/50"
              >
                <span className="text-sm font-medium text-primary-600">
                  Click to sign
                </span>
              </button>
            )}
          </FieldWrapper>
        );
      }

      case 'section_header':
        return (
          <div className="mb-2 mt-4 border-l-4 border-primary-500 py-2 pl-4">
            <h3 className="text-xl font-bold text-gray-900">{field.label}</h3>
            {field.helpText && (
              <p className="mt-1 text-sm text-gray-600">{field.helpText}</p>
            )}
          </div>
        );

      case 'divider':
        return (
          <div className="my-2">
            <hr className="border-gray-200" />
            {field.label && (
              <p className="mx-auto -mt-3 w-fit bg-white px-3 text-sm text-gray-500">
                {field.label}
              </p>
            )}
          </div>
        );

      case 'spacer':
        return <div className="my-4" />;

      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div
        className={
          layout === 'two-column'
            ? 'grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-2'
            : 'space-y-0'
        }
      >
        {fields.map((field) => (
          <div key={field.id} className={getFieldColSpan(field, layout)}>
            {renderField(field)}
          </div>
        ))}
      </div>

      <div className="mt-8">
        <Button
          type="submit"
          variant="default"
          className="w-full py-3"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Spinner size="sm" className="mr-2" />
              Submitting...
            </>
          ) : (
            submitButtonText
          )}
        </Button>
      </div>

      {/* Signature Capture Modal */}
      <SignatureCapture
        isOpen={!!activeSignatureField}
        onClose={() => setActiveSignatureField(null)}
        onSave={(data) => {
          if (activeSignatureField) {
            handleSignatureSave(activeSignatureField, data);
          }
        }}
        fieldLabel={
          activeSignatureField
            ? fields.find((f) => f.id === activeSignatureField)?.label
            : undefined
        }
      />
    </form>
  );
}
